import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import confetti from "canvas-confetti";
import { useGame } from "../GameState";
import { SFX, resumeAudio } from "../sfx";
import HUD, { AchievementPopup } from "../components/HUD";
import GameControls from "../components/GameControls";
import { MATCH_PHOTOS } from "../photoManifest";
import { MATCH_HEADER, MATCH_SUB, MATCH_ROUNDS } from "../MothersDayData";

const shuffleArr = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* Build rounds: pair each MATCH_ROUND with a photo, shuffle option order
   once per mount so it isn't deterministic between sessions. */
const buildRounds = () => {
  return MATCH_ROUNDS.map((r, i) => {
    const photo = MATCH_PHOTOS[i] || MATCH_PHOTOS[0];
    const options = shuffleArr([
      { text: r.correct, correct: true },
      ...r.decoys.map((d) => ({ text: d, correct: false })),
    ]);
    return { photo, options, afterglow: r.afterglow, correct: r.correct };
  });
};

const fireKissConfetti = () => {
  const colors = ["#ff6b9d", "#ffd93d", "#c44569", "#ff1493", "#ffa07a"];
  confetti({ particleCount: 45, spread: 65, startVelocity: 30, origin: { y: 0.55 }, colors, scalar: 0.95 });
};

const MatchAct = ({ onNext }) => {
  const { addScore, clearLevel, addAchievement } = useGame();
  const [phase, setPhase] = useState("ready"); // ready | playing | done
  const rounds = useMemo(buildRounds, []);
  const [roundIdx, setRoundIdx] = useState(0);
  const [picked, setPicked] = useState(null); // { idx, correct }
  const [wrongShake, setWrongShake] = useState(null); // option idx briefly shaken
  const [mistakes, setMistakes] = useState(0);
  const [time, setTime] = useState(0);
  const [scoreEarned, setScoreEarned] = useState(0);
  const [perfectStreak, setPerfectStreak] = useState(0); // consecutive first-try corrects
  const lockRef = useRef(false);

  /* timer */
  useEffect(() => {
    if (phase !== "playing") return;
    const id = setInterval(() => setTime((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const round = rounds[roundIdx];
  const lastRound = roundIdx >= rounds.length - 1;

  const start = () => {
    resumeAudio();
    SFX.click();
    setRoundIdx(0);
    setPicked(null);
    setWrongShake(null);
    setMistakes(0);
    setTime(0);
    setScoreEarned(0);
    setPerfectStreak(0);
    lockRef.current = false;
    setPhase("playing");
  };

  const onPick = (opt, idx) => {
    if (lockRef.current || phase !== "playing") return;
    if (opt.correct) {
      lockRef.current = true;
      setPicked({ idx, correct: true });
      SFX.match();
      fireKissConfetti();
      // streak: only if no mistakes this round
      const wasPerfect = !wrongShake;
      const newStreak = wasPerfect ? perfectStreak + 1 : 0;
      setPerfectStreak(newStreak);

      setTimeout(() => {
        if (lastRound) finish(newStreak);
        else nextRound();
      }, 2200);
    } else {
      setMistakes((m) => m + 1);
      setWrongShake(idx);
      SFX.bomb();
      setTimeout(() => setWrongShake(null), 600);
    }
  };

  const nextRound = () => {
    setPicked(null);
    setWrongShake(null);
    setRoundIdx((i) => i + 1);
    lockRef.current = false;
  };

  const finish = (finalStreak) => {
    // Score: 600 base − mistakes × 30 − time × 2, min 100
    const earned = Math.max(100, 600 - mistakes * 30 - time * 2);
    setScoreEarned(earned);
    addScore(earned);
    clearLevel("match", earned);
    addAchievement("matcher", "Heart Reader", "Read every line in his voice");
    if (mistakes === 0) {
      addAchievement("intuition", "Mother's Intuition", "Read every line first try");
    }
    if (finalStreak >= 3) {
      addAchievement("streak3", "In Sync", "3 perfect picks in a row");
    }
    SFX.win();
    setTimeout(() => setPhase("done"), 600);
  };

  return (
    <motion.div
      className="md-act md-act-game md-act-caption"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
    >
      <HUD level={3} levelLabel="Match the Moments" timer={time} />
      <AchievementPopup />

      <div className="md-act-header">
        <h1>{MATCH_HEADER}</h1>
        <div className="md-sub">
          {phase === "playing"
            ? `${roundIdx + 1} / ${rounds.length} · ${MATCH_SUB}`
            : MATCH_SUB}
        </div>
      </div>

      {phase === "playing" && (
        <GameControls
          onSkip={onNext}
          controls={[
            { label: "Each photo has three caption options below it." },
            { label: "Tap the line that sounds most like Aven." },
            { label: "Wrong picks shake gently — try again, no penalty for trying." },
            { label: "Score: 600 − mistakes × 30 − time × 2 (min 100)." },
          ]}
        />
      )}

      {phase === "playing" && round && (
        <div className="md-cap-stage">
          <AnimatePresence mode="wait">
            <motion.div
              key={`photo-${roundIdx}`}
              className="md-cap-photo-wrap"
              initial={{ opacity: 0, scale: 0.96, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 1.04, y: -18 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div
                className={`md-cap-photo ${picked?.correct ? "md-cap-glow" : ""}`}
                style={{ backgroundImage: `url(${round.photo})` }}
              />

              <AnimatePresence>
                {picked?.correct && (
                  <motion.div
                    className="md-cap-bubble"
                    initial={{ opacity: 0, scale: 0.7, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ type: "spring", stiffness: 220, damping: 18 }}
                  >
                    <div className="md-cap-bubble-from">aven says</div>
                    <div className="md-cap-bubble-text">{round.correct}</div>
                    <div className="md-cap-bubble-after">{round.afterglow}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>

          <div className="md-cap-options" key={`opts-${roundIdx}`}>
            {round.options.map((opt, i) => {
              const showRight = picked?.correct && opt.correct;
              const showShake = wrongShake === i;
              const dim = picked?.correct && !opt.correct;
              return (
                <motion.button
                  key={`${roundIdx}-${i}`}
                  type="button"
                  className={[
                    "md-cap-opt",
                    showRight ? "md-cap-opt-right" : "",
                    dim ? "md-cap-opt-dim" : "",
                  ].join(" ")}
                  onClick={() => onPick(opt, i)}
                  disabled={lockRef.current}
                  initial={{ opacity: 0, y: 10 }}
                  animate={
                    showShake
                      ? { x: [-8, 8, -6, 6, -3, 3, 0], opacity: 1, y: 0 }
                      : { opacity: 1, y: 0 }
                  }
                  transition={{ delay: 0.05 * i, duration: 0.45 }}
                >
                  <span className="md-cap-opt-quote">“</span>
                  {opt.text}
                </motion.button>
              );
            })}
          </div>

          <div className="md-cap-meta">
            <span className="md-pill">round {roundIdx + 1} / {rounds.length}</span>
            <span className="md-pill">misses {mistakes}</span>
            {perfectStreak >= 2 && (
              <span className="md-pill md-pill-gold">streak ×{perfectStreak}</span>
            )}
          </div>
        </div>
      )}

      <AnimatePresence>
        {phase === "ready" && (
          <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="md-game-card">
              <div className="md-game-badge">Level 3</div>
              <h2>What is Aven saying?</h2>
              <p className="md-game-tagline">
                One photo. Three captions.<br />
                Tap the one that sounds most like him.
              </p>
              <ul className="md-game-rules">
                <li>6 photos · pick the line that's really Aven</li>
                <li>Wrong picks just shake — try again, no death</li>
                <li>Achievements: <em>Mother's Intuition</em> (zero misses), <em>In Sync</em> (3 perfect in a row)</li>
              </ul>
              <div className="md-game-actions">
                <button className="md-btn-soft md-cta" onClick={start}>Start Level 3 →</button>
                <button className="md-btn-soft md-btn-ghost" onClick={onNext}>Skip</button>
              </div>
            </div>
          </motion.div>
        )}

        {phase === "done" && (
          <motion.div className="md-game-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="md-game-card md-game-win">
              <div className="md-game-badge md-badge-gold">+{scoreEarned}</div>
              <h2>You read every word in his voice.</h2>
              <p className="md-game-aven-says">
                ‘Mama, you always know what I mean — even when I don't say it.’
              </p>
              <div className="md-final-stats">
                <div><strong>{rounds.length}</strong> moments</div>
                <div><strong>{mistakes}</strong> misses</div>
                <div><strong>{time}s</strong></div>
              </div>
              <button className="md-btn-soft md-cta" onClick={onNext}>Next: Boss Level →</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default MatchAct;
